import type { ObjectId } from "@novorender/data-js-api";
import type { ReadonlyVec3 } from "gl-matrix";

import { base64ToArrayBuffer } from "utils/misc";

import { procoreClient } from "./client";
import { throwWhenBimQueryBackfilling as throwWhenBackfilling } from "./errors";
import type { SearchPredicate } from "./searchPredicate";

/**
 * Procore's **BIM Query API** — the bim-index engine served over HTTP.
 *
 * The same index `features/bimIndex` reads out of OPFS, answered by a service that has
 * the files mounted instead. It is published under `/rest/v2.1` rather than the `v2.0`
 * the rest of the BIM API uses, to separate the index-backed query surface from the
 * endpoints it replaces — the `v2.0` `search`/`objects`/`resolve` trio in
 * `bimObjectSearch.ts`, `bimObjectMetadata.ts` and `bimObjectResolve.ts`, which
 * bim-search-db still serves.
 *
 * @see https://developers.procore.com/reference/rest/bim-objects
 * @see procore/bim-platform-service `src/Procore.Bim.Platform.Api/Features/BimQuery`
 *
 * Two request-wide choices are made here rather than exposed:
 *
 * - **`id_kind=INTERNAL`.** The API addresses an object either by its 32-char hex
 *   Procore/InstanceId or by the engine's own `u32`, and the latter is the viewer's
 *   `ObjectId` — so internal ids are both what every caller of this module already
 *   holds and the cheaper walk (no external-id lookup in, no batch resolution out).
 *   The API's own default is `EXTERNAL`, so a request that needs the encoding named states
 *   it. The ones that do not are the reads whose ids are a JSON array in the body, which
 *   names their encoding by element type — `objects/expand`, `objects/resolve`, and
 *   `objects/search` under the id-set representation, whose answer is internal ids by
 *   construction. Each says so where it sends nothing.
 * - **`view`**, which decides how much of each row comes back: bare ids (`ID`),
 *   id/path/name/`has_children` (`LIGHT`), or those plus properties (`FULL`). Each
 *   function below asks for the least its caller needs.
 *
 * A read that wants a whole *id set* rather than rows asks for a different
 * representation instead of a wider page — see {@link BIM_QUERY_ID_SET_MEDIA_TYPE}.
 *
 * Every read can answer `202` with a pending body when the scene version's index is not
 * yet on the service's mount, so all of them map it to `SceneBackfillingError` through
 * {@link throwWhenBackfilling} — {@link probeBimQuery} being the one deliberate
 * exception, for the reason given there.
 *
 * Every function takes the {@link BimQueryTarget} it addresses as its own first argument,
 * separately from what the call itself asks for. The target is fixed for a whole session
 * while the rest changes per request, so keeping them apart lets a caller hold one and
 * spread neither.
 */

/** Which scene version a BIM Query read addresses. */
export interface BimQueryTarget {
    companyId: number;
    projectId: number;
    /** The Procore scene id. The route constrains this segment to a guid. */
    sceneId: string;
    /** A scene version id, or the literal `current`. */
    sceneVersionId: string;
    /**
     * A slice of {@link sceneVersionId} — the subset of the version a Snapshot is taken
     * of. Narrows every read to the slice's own objects; absent means the whole version.
     *
     * A query parameter rather than a path segment because the index being read is the
     * version's: the service filters it down to the slice, so the route is the same one
     * a version-level session addresses.
     */
    sceneSliceId?: string;
}

/**
 * The id segment that names the virtual super-root above the top-level nodes, so
 * listing the top of the tree is the same call as expanding any other node. It cannot
 * collide with a real id: internal ids are decimal digits.
 */
export const BIM_QUERY_ROOT = "root";

/**
 * Largest `per_page` the tree navigation reads accept — the same ceiling as
 * {@link BIM_QUERY_OBJECT_PAGE_LIMIT}, not the framework's default cap of 100 these
 * routes were first written against.
 *
 * Worth stating because it is easy to regress from outside this client: an intermediary
 * that imposes the default cap rejects a larger page outright, and then *every* folder
 * listing and ancestor walk comes back empty rather than short. Suspect that before
 * suspecting a paging bug here.
 */
export const BIM_QUERY_TREE_PAGE_LIMIT = 1000;

/** Largest `per_page` the bulk object reads accept — search and descendants. */
export const BIM_QUERY_OBJECT_PAGE_LIMIT = 1000;

/**
 * The `Accept` that asks a bitmap-backed read for its **whole result set** as
 * portable roaring bytes instead of a page of JSON.
 *
 * Requests returning bitmaps ignore view and page query params -
 * they always return only internal IDs as a single bitmap.
 *
 * `version=1` is stated rather than left off: it is the only version the service
 * serves, and pinning it means a future format ships as a `406` here instead of as
 * bytes this app would misread.
 */
export const BIM_QUERY_ID_SET_MEDIA_TYPE = "application/vnd.procore.bim.roaring; version=1";

const MAX_PAGE_ITERATIONS = 500;

/**
 * A whole result set, as the bitmap representation answers it.
 *
 * The bytes are not decoded here. Deserializing them is what the BIM-index WASM is
 * for, and it lives in a worker (`features/bimIndex`) — so this module stays a
 * transport and the ids are materialized off the main thread. See
 * `bimIndexBitmapIds`.
 */
export interface BimQueryIdSet {
    /** The response body verbatim: the whole set, portable roaring format. */
    bytes: Uint8Array;
    /**
     * How many objects the set holds, from the `Total` header — roaring bytes do not
     * disclose their cardinality without being decoded, and this is what a caller
     * sizes a UI from before it decodes anything.
     */
    total: number | undefined;
    /** `Bim-Degraded`: the read covered less than the request asked for. */
    degraded: boolean;
    /** `Bim-Degradations`: why it narrowed, one entry per distinct cause. */
    degradations: string[];
    /**
     * `Bim-Resume-Token`: sent back unchanged as `resumeToken` to pick up the matches
     * a cut-short scan never reached. Present whenever the read can be continued,
     * which is not the same question as {@link degraded}.
     */
    resumeToken: string | undefined;
}

/**
 * An id list as JSON: internal ids as numbers, `Procore/InstanceId`s as strings — the
 * element type says which. {@link BimQueryRoaringIds} is the wider alternative.
 */
export type BimQueryIds = readonly ObjectId[] | readonly string[] | Uint32Array;

/**
 * Internal ids as portable roaring bytes, base64 on the wire.
 *
 * @see `features/bimIndex/session`'s `bimIndexIdsBitmap`, which builds them.
 */
export interface BimQueryRoaringIds {
    roaring: Uint8Array;
}

/**
 * How many ids one {@link BimQueryRoaringIds} may hold.
 * There's no actual limit at the moment - the only limits are payload size and request timeout.
 * Payload size is hard to hit with roaring, because larger payloads are more contiguous and more compressible.
 * Request timeout was also increased to 10 seconds, so BIM platform suggests to remove the limit altogether,
 * but I'll keep it just in case for now.
 *
 * Note that for some endpoints (e.g. /collapse) splitting the requests into chunks is not possible,
 * because it changes the result, so they might omit the limit altogether.
 */
export const BIM_QUERY_ROARING_ID_LIMIT = 10_000_000;

/**
 * How many ids {@link expandBimQueryIdSet} sends in one request **as an array**.
 *
 * **Ours, not the API's.** The endpoint places no ceiling on the list: it is bounded by
 * the maximum request body size, past which the framework answers `413` rather than
 * anything a caller can act on. So the client picks a width that cannot reach that
 * bound. An internal id is at most ten digits plus a separator, which puts this at
 * roughly 550 KB of body — comfortably inside a default limit and inside the 1 MB an
 * intermediary is most likely to impose.
 *
 * Raise it only against a known configured limit. Nothing breaks at a smaller value
 * except request count, since the union of the batches is the union of the whole.
 */
export const BIM_QUERY_EXPAND_ID_LIMIT = 50_000;

/**
 * How many ids {@link getBimQueryBounds} sends in one request.
 *
 * {@link BIM_QUERY_EXPAND_ID_LIMIT}'s reasoning exactly — the endpoint places no ceiling
 * on the id list either, so the client picks a width that cannot reach the request-body
 * bound. Separate from it only so that raising one against a measured limit does not
 * silently move the other.
 */
export const BIM_QUERY_BOUNDS_ID_LIMIT = 50_000;

/**
 * How many ids one id translation sends — see {@link resolveBimQueryInstanceIds}.
 *
 * {@link BIM_QUERY_EXPAND_ID_LIMIT}'s reasoning, with one difference worth stating: the
 * *service* batches its own native reads at this same figure, chosen to match what this
 * client sends. So the common request is one native call per direction, and a value raised
 * here only costs the service an extra batch rather than failing.
 */
export const BIM_QUERY_RESOLVE_ID_LIMIT = 50_000;

/** An id set collapsed to the subtrees standing for it plus the members no subtree absorbed. */
export interface BimQueryCollapsedIdSets {
    /** Each id stands for its whole subtree, itself included. Portable roaring bytes. */
    subtrees: Uint8Array;
    /** Each id stands only for itself. Portable roaring bytes. */
    objects: Uint8Array;
    /** `meta.degraded`: the answer names more ids than it needed to, or misses some. */
    degraded: boolean;
    /** `meta.degradations[].kind`, one entry per distinct cause. */
    degradations: string[];
}

/** One node of the object tree: a parser's object, or a grouping row above them. */
export interface BimQueryTreeNode {
    id: ObjectId;
    /** Display name — the leaf segment of the node's path. The API sends no path here. */
    name: string;
    /** Objects anywhere in this node's subtree, the node included. */
    descendantCount: number;
    hasChildren: boolean;
}

/** Bounding volume of an object, in the shape `ObjectData.bounds` wants. */
export interface BimQueryBounds {
    box: { min: ReadonlyVec3; max: ReadonlyVec3 };
    sphere: { center: ReadonlyVec3; radius: number };
}

/**
 * How much of each row a read that answers objects asks for — see the module comment.
 * `FULL` is `LIGHT` plus the properties.
 */
export type BimQueryObjectView = "LIGHT" | "FULL";

/** Which enclosing shape(s) a bounds read computes. `BOX` is the API's own default. */
export type BimQueryBoundsParts = "BOX" | "SPHERE" | "BOTH";

/**
 * Which matching ancestor a climb resolves to. `NEAREST` is the API's own default and is
 * the deepest match — Navisworks' "last object"; `TOPMOST` is the shallowest, its "first
 * object".
 */
export type BimQueryClimbPolicy = "NEAREST" | "TOPMOST";

/**
 * The volume enclosing a selection — a *computed* fold, so unlike {@link BimQueryBounds}
 * both halves are optional: only the part(s) the request asked for are populated, and
 * each is folded independently over the selection's members, so neither necessarily
 * encloses the other.
 */
export interface BimQueryComputedBounds {
    box?: { min: ReadonlyVec3; max: ReadonlyVec3 };
    sphere?: { center: ReadonlyVec3; radius: number };
}

/** One object of the index. `properties` is populated only for a `FULL` read. */
export interface BimQueryObject {
    id: ObjectId;
    /** Full `/`-delimited path in the scene hierarchy. */
    path: string;
    /** The leaf segment of {@link path}. */
    name: string;
    /** Depth in the tree, 0 at the root. */
    level: number;
    /**
     * Whether the object has children in the hierarchy — a grouping node rather than a
     * leaf. The same flag the tree rows carry, on the object itself, so a row that never
     * came from the tree still knows its own node type. Sent for `LIGHT` and `FULL`
     * alike, which is every read that answers objects rather than bare ids.
     */
    hasChildren: boolean;
    bounds?: BimQueryBounds;
    properties: [string, string][];
}

/** Always internal ids — see the module comment. */
const ID_KIND = "INTERNAL";

/** A JSON response's `meta`; only the members read here are declared. */
type Envelope<T> = {
    data: T;
    meta?: {
        degraded?: boolean;
        /** One entry per distinct cause, each naming its `kind`. */
        degradations?: { kind: string }[] | null;
        resume_token?: string | null;
    };
};

/**
 * Integers wide enough to need it are declared `["integer", "string"]` in the spec, so
 * a count or an id may arrive either way and every numeric field is coerced.
 */
type WireNumber = number | string;

interface WireTreeNode {
    id: WireNumber;
    name: string;
    descendant_count: WireNumber;
    has_children: boolean;
}

interface WireVector3 {
    x: number;
    y: number;
    z: number;
}

interface WireObject {
    /** Null only under `id_kind=external`, for a mapping the index cannot resolve. */
    id: WireNumber | null;
    path: string;
    name: string;
    level: WireNumber;
    has_children: boolean;
    bounds?: {
        box: { min: WireVector3; max: WireVector3 };
        sphere?: { center: WireVector3; radius: WireNumber } | null;
    } | null;
    properties?: [string, string][] | null;
}

interface WireComputedBounds {
    box?: { min: WireVector3; max: WireVector3 } | null;
    sphere?: { center: WireVector3; radius: WireNumber } | null;
}

/**
 * The body `climb` and `selection` share, which is what lets a caller send both from one
 * set of arguments.
 *
 * `ids` stay numbers: the endpoints read the encoding off the array's JSON element type, so
 * strings would be taken for external hex ids and rejected. Absent members are omitted
 * rather than sent as `null`, which the optional predicates would bind as malformed.
 */
function climbBody(params: {
    ids: BimQueryIds | BimQueryRoaringIds;
    targetPredicate: SearchPredicate;
    stopPredicate?: SearchPredicate;
    includeSelf?: boolean;
}): Record<string, unknown> {
    return {
        ids: idsField(params.ids),
        target_predicate: params.targetPredicate,
        ...(params.stopPredicate === undefined ? {} : { stop_predicate: params.stopPredicate }),
        ...(params.includeSelf === undefined ? {} : { include_self: params.includeSelf }),
    };
}

/** @see BimQueryIds */
function idsField(ids: BimQueryIds | BimQueryRoaringIds): ObjectId[] | string[] | { roaring: string } {
    return isRoaring(ids) ? { roaring: base64(ids.roaring) } : ([...ids] as ObjectId[] | string[]);
}

function isRoaring(ids: BimQueryIds | BimQueryRoaringIds): ids is BimQueryRoaringIds {
    return "roaring" in ids;
}

/**
 * Standard (not URL-safe) base64. Chunked: `String.fromCharCode(...bytes)` over a wide
 * bitmap overflows the call stack.
 */
function base64(bytes: Uint8Array): string {
    const CHUNK = 0x8000;
    let binary = "";
    for (let at = 0; at < bytes.length; at += CHUNK) {
        binary += String.fromCharCode(...bytes.subarray(at, at + CHUNK));
    }
    return btoa(binary);
}

function sceneVersionUrl(target: BimQueryTarget): string {
    return `/rest/v2.1/companies/${target.companyId}/projects/${target.projectId}/bim/scenes/${target.sceneId}/versions/${target.sceneVersionId}`;
}

/**
 * `slice_id` for a session opened on a slice, and nothing for a version-level one — so
 * every read below narrows the same way without each having to ask whether to.
 */
function sliceParams(target: BimQueryTarget): { slice_id?: string } {
    return target.sceneSliceId ? { slice_id: target.sceneSliceId } : {};
}

function vec3(vector: WireVector3): ReadonlyVec3 {
    // The API sends `{x, y, z}` objects; everything downstream of here wants an array.
    return [vector.x, vector.y, vector.z];
}

/**
 * `ObjectData.bounds` carries both a box and a sphere, and the API's sphere is optional,
 * so one is derived from the box when it is absent — a consumer drawing or framing an
 * object should not have to know which half the index happened to store.
 */
function parseBounds(bounds: NonNullable<WireObject["bounds"]>): BimQueryBounds {
    const box = { min: vec3(bounds.box.min), max: vec3(bounds.box.max) };
    if (bounds.sphere) {
        return { box, sphere: { center: vec3(bounds.sphere.center), radius: Number(bounds.sphere.radius) } };
    }
    const center: ReadonlyVec3 = [
        (box.min[0] + box.max[0]) / 2,
        (box.min[1] + box.max[1]) / 2,
        (box.min[2] + box.max[2]) / 2,
    ];
    const radius = Math.hypot(box.max[0] - box.min[0], box.max[1] - box.min[1], box.max[2] - box.min[2]) / 2;
    return { box, sphere: { center, radius } };
}

/**
 * Deliberately not {@link parseBounds}: that derives a sphere from the box when the API
 * sends none, which is right for an object that has one volume expressed two ways, and
 * wrong here — an absent part was simply not asked for.
 */
function parseComputedBounds(bounds: WireComputedBounds): BimQueryComputedBounds {
    const computed: BimQueryComputedBounds = {};
    if (bounds.box) {
        computed.box = { min: vec3(bounds.box.min), max: vec3(bounds.box.max) };
    }
    if (bounds.sphere) {
        computed.sphere = { center: vec3(bounds.sphere.center), radius: Number(bounds.sphere.radius) };
    }
    return computed;
}

function parseTreeNode(node: WireTreeNode): BimQueryTreeNode {
    return {
        id: Number(node.id) as ObjectId,
        name: node.name,
        descendantCount: Number(node.descendant_count),
        hasChildren: node.has_children,
    };
}

function parseObject(object: WireObject): BimQueryObject | undefined {
    if (object.id === null) {
        return undefined;
    }
    return {
        id: Number(object.id) as ObjectId,
        path: object.path,
        name: object.name,
        level: Number(object.level),
        hasChildren: object.has_children,
        bounds: object.bounds ? parseBounds(object.bounds) : undefined,
        properties: object.properties ?? [],
    };
}

/**
 * The `cursor` of the response's `rel="next"` link, or `null` at the end of a collection.
 *
 * The tree reads are keyset-paged and carry their cursor in the `Link` header rather
 * than the body, so it is read here alongside the rows. Splitting on `,` is safe for
 * these links: the cursor is base64url and the rest of the URL is fixed by the route.
 */
function nextCursorFrom(response: Response): string | null {
    const header = response.headers.get("Link");
    if (!header) {
        return null;
    }
    for (const part of header.split(",")) {
        if (!/;\s*rel\s*=\s*"?next"?/i.test(part)) {
            continue;
        }
        const url = part.match(/<([^>]+)>/)?.[1];
        if (!url) {
            continue;
        }
        try {
            // Relative against the API host, since a link may arrive either way and only
            // the query string is wanted.
            return new URL(url, "https://api.procore.com").searchParams.get("cursor");
        } catch {
            return null;
        }
    }
    return null;
}

/**
 * A bitmap response: the body as bytes, and the four headers that carry what a JSON
 * response would have put in `meta`.
 */
async function parseIdSet(response: Response): Promise<BimQueryIdSet> {
    const bytes = new Uint8Array(await response.arrayBuffer());
    const degradations = response.headers.get("Bim-Degradations");
    return {
        bytes,
        total: totalFrom(response),
        // Sent only when true, as `meta` is omitted from a clean paged response.
        degraded: response.headers.get("Bim-Degraded") === "true",
        degradations: degradations ? degradations.split(",").map((kind) => kind.trim()) : [],
        resumeToken: response.headers.get("Bim-Resume-Token") ?? undefined,
    };
}

/** The `Total` header, when the collection reports one. */
function totalFrom(response: Response): number | undefined {
    const header = response.headers.get("Total");
    if (header === null) {
        return undefined;
    }
    const total = Number(header);
    return Number.isFinite(total) ? total : undefined;
}

/** core-http rejects non-2xx with an error carrying the status. */
function isNotFound(error: unknown): boolean {
    return (error as { status?: number } | null)?.status === 404;
}

/**
 * One object with its properties, or `undefined` when the id names nothing in this
 * scene version.
 */
export async function getBimQueryObject(
    target: BimQueryTarget,
    params: { id: ObjectId; signal?: AbortSignal },
): Promise<BimQueryObject | undefined> {
    try {
        const response = await procoreClient<Envelope<WireObject>>(`${sceneVersionUrl(target)}/objects/${params.id}`, {
            method: "GET",
            params: { id_kind: ID_KIND, view: "FULL", ...sliceParams(target) },
            parseResponse: throwWhenBackfilling<Envelope<WireObject>>((res) => res.json()),
            signal: params.signal,
        });
        return parseObject(response.data);
    } catch (e) {
        // A 404 is "no such object", which every caller here treats as an absence
        // rather than a failure — the same contract `ObjectDataAccess.getObject` states.
        if (isNotFound(e)) {
            return undefined;
        }
        throw e;
    }
}

export interface BimQueryChildrenPage {
    items: BimQueryTreeNode[];
    nextCursor: string | null;
    /** The level is short and cannot be paged further: a full page carried no `next` link. */
    truncated: boolean;
}

/**
 * One page of a node's immediate children, and the cursor that continues it.
 *
 * `id` is a node id or {@link BIM_QUERY_ROOT} for the top of the tree. There is no
 * total: the engine counts a level only by walking it.
 */
export async function listBimQueryChildren(
    target: BimQueryTarget,
    params: {
        id: ObjectId | typeof BIM_QUERY_ROOT;
        cursor?: string | null;
        perPage?: number;
        signal?: AbortSignal;
    },
): Promise<BimQueryChildrenPage> {
    const perPage = params.perPage ?? BIM_QUERY_TREE_PAGE_LIMIT;
    return procoreClient<BimQueryChildrenPage>(`${sceneVersionUrl(target)}/objects/${params.id}/children`, {
        method: "GET",
        params: {
            id_kind: ID_KIND,
            per_page: perPage,
            ...sliceParams(target),
            ...(params.cursor ? { cursor: params.cursor } : {}),
        },
        parseResponse: throwWhenBackfilling(async (res) => {
            const body = (await res.json()) as Envelope<WireTreeNode[]>;
            const items = body.data.map(parseTreeNode);
            const nextCursor = nextCursorFrom(res);
            // A full page with nowhere to go next is the signature of a `Link`
            // header that did not reach us — and there is no other way to ask
            // for the rest of a keyset-paged level, so the level is silently
            // short. Say so rather than passing it off as complete.
            const truncated = !nextCursor && items.length >= perPage;
            if (truncated) {
                console.warn(
                    `[bim-query] a full page of children for ${params.id} carried no "next" link; the rest of this level cannot be paged`,
                );
            }
            return { items, nextCursor, truncated };
        }),
        signal: params.signal,
    });
}

/**
 * Every immediate child of `id` ({@link BIM_QUERY_ROOT} for the top level), paged to the
 * end. `undefined` when it could not reach the end — {@link BimQueryChildrenPage.truncated}
 * or the page cap — since a level that is short without saying so becomes a wrong count
 * downstream rather than an absent one.
 */
export async function listAllBimQueryChildren(
    target: BimQueryTarget,
    params: {
        id: ObjectId | typeof BIM_QUERY_ROOT;
        signal?: AbortSignal;
    },
): Promise<BimQueryTreeNode[] | undefined> {
    const items: BimQueryTreeNode[] = [];
    let cursor: string | null = null;
    for (let i = 0; i < MAX_PAGE_ITERATIONS; i++) {
        const page = await listBimQueryChildren(target, {
            id: params.id,
            signal: params.signal,
            perPage: BIM_QUERY_TREE_PAGE_LIMIT,
            cursor,
        });
        items.push(...page.items);
        if (page.truncated) {
            return undefined;
        }
        cursor = page.nextCursor;
        if (!cursor) {
            return items;
        }
    }
    console.warn(
        `[bim-query] children of ${params.id} still had pages after ${MAX_PAGE_ITERATIONS}; the rest of this level was not read`,
    );
    return undefined;
}

/**
 * A node's ancestor chain, **nearest first** — the immediate parent leads and the
 * top-level node ends it. Not paged: it is bounded by the tree's depth, and the API
 * has no separate parent endpoint because the first element is that answer.
 */
export async function listBimQueryAncestors(
    target: BimQueryTarget,
    params: { id: ObjectId; signal?: AbortSignal },
): Promise<BimQueryTreeNode[]> {
    const response = await procoreClient<Envelope<WireTreeNode[]>>(
        `${sceneVersionUrl(target)}/objects/${params.id}/ancestors`,
        {
            method: "GET",
            params: { id_kind: ID_KIND, ...sliceParams(target) },
            parseResponse: throwWhenBackfilling<Envelope<WireTreeNode[]>>((res) => res.json()),
            signal: params.signal,
        },
    );
    return response.data.map(parseTreeNode);
}

/**
 * The **whole** subtree beneath a node, transitively and **excluding the node
 * itself**, as one bitmap.
 *
 * As on `objects/expand`, `id_kind` is rejected on this operation, so the `{id}` segment
 * goes as the internal id it already is and the query string carries the slice or
 * nothing at all. The page parameters are rejected too — the whole set comes back as one
 * bitmap, so there is no page to describe.
 */
export async function listBimQueryDescendantIdSet(
    target: BimQueryTarget,
    params: { id: ObjectId; signal?: AbortSignal },
): Promise<BimQueryIdSet> {
    const query = sliceParams(target);
    return procoreClient<BimQueryIdSet>(`${sceneVersionUrl(target)}/objects/${params.id}/descendants`, {
        method: "GET",
        headers: { Accept: BIM_QUERY_ID_SET_MEDIA_TYPE },
        ...(Object.keys(query).length ? { params: query } : {}),
        parseResponse: throwWhenBackfilling(parseIdSet),
        signal: params.signal,
    });
}

/**
 * One page of the objects matching `predicate`, in ascending internal-id order — a
 * stable snapshot, so paging never repeats or skips a row.
 *
 * `degraded` says the query may have covered less than it was asked for, which the
 * engine reports as metadata on a `200` rather than as an error: the rows returned are
 * correct, but rows are missing with nothing to name them.
 *
 * @param params.view Defaults to `LIGHT`, which carries id, path and name — a row,
 * without the property read a `FULL` page costs for every match. Worth `FULL` only for a
 * narrow page whose rows are wanted as objects.
 */
export async function searchBimQueryObjects(
    target: BimQueryTarget,
    params: {
        predicate: SearchPredicate;
        scopeId?: ObjectId;
        page: number;
        perPage?: number;
        view?: BimQueryObjectView;
        signal?: AbortSignal;
    },
): Promise<{ items: BimQueryObject[]; degraded: boolean }> {
    return procoreClient<{ items: BimQueryObject[]; degraded: boolean }>(`${sceneVersionUrl(target)}/objects/search`, {
        method: "POST",
        body: {
            predicate: params.predicate,
            ...(params.scopeId === undefined ? {} : { scope_id: String(params.scopeId) }),
        },
        params: {
            id_kind: ID_KIND,
            view: params.view ?? "LIGHT",
            page: params.page,
            per_page: params.perPage ?? BIM_QUERY_OBJECT_PAGE_LIMIT,
            ...sliceParams(target),
        },
        parseResponse: throwWhenBackfilling(async (res) => {
            const body = (await res.json()) as Envelope<WireObject[]>;
            const items = body.data.map(parseObject).filter((object): object is BimQueryObject => object !== undefined);
            return { items, degraded: body.meta?.degraded === true };
        }),
        signal: params.signal,
    });
}

/**
 * The **whole** match set for `predicate` as one bitmap — the same read as
 * {@link searchBimQueryObjects}, for a caller that wants the ids and not the rows.
 *
 * Separate rather than a `view` parameter because it is a different representation,
 * not a different row shape: one response carries the entire set, so nothing is paged
 * and `view` itself is one of the parameters this must not send.
 *
 * `resumeToken` continues a search the engine's scan deadline cut short, recovering
 * matches it never reached — it *widens* the set rather than advancing through it,
 * which is why the answer to a resumed read is another whole set to union rather than
 * the next page of this one.
 */
export async function searchBimQueryIdSet(
    target: BimQueryTarget,
    params: {
        predicate: SearchPredicate;
        scopeId?: ObjectId;
        resumeToken?: string;
        signal?: AbortSignal;
    },
): Promise<BimQueryIdSet> {
    return procoreClient<BimQueryIdSet>(`${sceneVersionUrl(target)}/objects/search`, {
        method: "POST",
        headers: { Accept: BIM_QUERY_ID_SET_MEDIA_TYPE },
        body: {
            predicate: params.predicate,
            // A JSON *number*, unlike the string {@link searchBimQueryObjects} sends on
            // this same route: there `id_kind` names the encoding, and here there is no
            // `id_kind` to name it with, so the type has to. `"7"` under the API's own
            // `EXTERNAL` default would scope to whatever object carries the external id
            // `7` — an empty set rather than object 7's subtree.
            ...(params.scopeId === undefined ? {} : { scope_id: params.scopeId }),
        },
        // No `id_kind`, unlike {@link listBimQueryDescendantIdSet}: the only id on the way
        // in is `scope_id`, and the body names its encoding by JSON type the way
        // `objects/expand` does with `ids`.
        params: {
            ...sliceParams(target),
            ...(params.resumeToken === undefined ? {} : { resume_token: params.resumeToken }),
        },
        parseResponse: throwWhenBackfilling(parseIdSet),
        signal: params.signal,
    });
}

/**
 * The union of the given objects' subtrees as one bitmap — every object the ids stand
 * for, in one request rather than one per root.
 *
 * @param params.ids Roots to expand — at most {@link BIM_QUERY_EXPAND_ID_LIMIT} per request.
 * @param params.includeSelf Include the provided ids (root nodes), whether or not each
 *   owns a tree node to walk. Defaults to `true`.
 * @param params.resumeToken Continues an expansion the scan deadline cut short, as on
 *   {@link searchBimQueryIdSet}: the answer is another whole set to union.
 */
export async function expandBimQueryIdSet(
    target: BimQueryTarget,
    params: {
        ids: BimQueryIds | BimQueryRoaringIds;
        includeSelf?: boolean;
        resumeToken?: string;
        signal?: AbortSignal;
    },
): Promise<BimQueryIdSet> {
    // The only read here whose query string can be empty: `id_kind` is rejected on this
    // operation, so with no slice, no opinion on `include_self` and nothing to resume there
    // is nothing to send.
    const query = {
        ...sliceParams(target),
        ...(params.includeSelf === undefined ? {} : { include_self: params.includeSelf }),
        ...(params.resumeToken === undefined ? {} : { resume_token: params.resumeToken }),
    };
    return procoreClient<BimQueryIdSet>(`${sceneVersionUrl(target)}/objects/expand`, {
        method: "POST",
        headers: { Accept: BIM_QUERY_ID_SET_MEDIA_TYPE },
        body: { ids: idsField(params.ids) },
        ...(Object.keys(query).length ? { params: query } : {}),
        parseResponse: throwWhenBackfilling(parseIdSet),
        signal: params.signal,
    });
}

/**
 * `objects/collapse` — the inverse of {@link expandBimQueryIdSet}: `ids` reduced to the
 * subtrees that stand for them plus the members no subtree absorbed. The subtrees may name
 * grouping nodes `ids` did not.
 *
 * `format=BITMAPS` rather than paged rows: both sets come back whole in one response. It is
 * JSON with base64 bitmaps inside, so this sends no roaring `Accept`, which the operation
 * answers with `406`.
 *
 * The ids go up as a bitmap too, so the whole set fits one request: a collapse does not
 * compose across batches, since a subtree split between two requests comes back as its
 * members rather than as the subtree.
 *
 * @param params.ids Internal ids as portable roaring bytes.
 */
export async function collapseBimQueryIdSet(
    target: BimQueryTarget,
    params: { ids: Uint8Array; signal?: AbortSignal },
): Promise<BimQueryCollapsedIdSets> {
    type WireCollapsed = {
        data: { subtrees: { roaring: string }; objects: { roaring: string } };
        meta?: { degraded?: boolean; degradations?: { kind: string }[] };
    };
    return procoreClient<BimQueryCollapsedIdSets>(`${sceneVersionUrl(target)}/objects/collapse`, {
        method: "POST",
        body: { ids: { roaring: bytesToBase64(params.ids) } },
        // No `id_kind`: it is rejected under `format=BITMAPS`, whose sets are internal ids.
        params: { format: "BITMAPS", ...sliceParams(target) },
        parseResponse: throwWhenBackfilling(async (res) => {
            const body = (await res.json()) as WireCollapsed;
            return {
                subtrees: new Uint8Array(base64ToArrayBuffer(body.data.subtrees.roaring)),
                objects: new Uint8Array(base64ToArrayBuffer(body.data.objects.roaring)),
                degraded: body.meta?.degraded === true,
                degradations: body.meta?.degradations?.map((d) => d.kind) ?? [],
            };
        }),
        signal: params.signal,
    });
}

function bytesToBase64(bytes: Uint8Array): string {
    // Chunked: spreading a large array into `fromCharCode` overflows the call stack.
    const CHUNK = 0x8000;
    let binary = "";
    for (let at = 0; at < bytes.length; at += CHUNK) {
        binary += String.fromCharCode(...bytes.subarray(at, at + CHUNK));
    }
    return btoa(binary);
}

/**
 * `objects/resolve` — the one endpoint that exposes the id mapping every other read
 * applies implicitly through `id_kind`.
 *
 * **Index-aligned with the request**: entry `i` answers the `i`-th id sent, a repeated id
 * is answered once per position, and an id the scene version carries no object for answers
 * `null` rather than being left out — so the two arrays always zip. Not paged, since a page
 * of a positional answer could not be aligned with anything.
 *
 * The direction is named by the array's JSON element type — numbers in, hex strings out —
 * which is why the two directions are two functions here rather than one with a flag, and
 * why there is no `id_kind` on this operation: the answer is by definition the encoding the
 * request is not in.
 *
 * **Every id must be a `u32`.** The service validates the whole array and answers `422` for
 * one malformed entry, so a caller holding ids it has not vetted must filter before calling
 * — see `BimIndexDataAccess`.
 *
 * `POST` rather than the `QUERY` the API also routes this on: `QUERY` is undocumented on
 * purpose and needs a fallback anyway.
 *
 * Narrowed to the slice like every other read, so a slice session's mapping answers for
 * the objects that session can see and `null` for the rest — the same answer the version
 * gives for an id it carries no object for, and the one the alignment contract above
 * already makes every caller handle.
 */
export async function resolveBimQueryInstanceIds(
    target: BimQueryTarget,
    params: { objectIds: readonly ObjectId[]; signal?: AbortSignal },
): Promise<(string | null)[]> {
    const query = sliceParams(target);
    const response = await procoreClient<Envelope<(string | null)[]>>(`${sceneVersionUrl(target)}/objects/resolve`, {
        method: "POST",
        headers: { Accept: "application/json" },
        // As on `objects/expand`, a JSON array of numbers *is* the internal-id
        // encoding — and here it is also what selects the direction.
        body: { ids: [...params.objectIds] },
        ...(Object.keys(query).length ? { params: query } : {}),
        parseResponse: throwWhenBackfilling<Envelope<(string | null)[]>>((res) => res.json()),
        signal: params.signal,
    });
    return response.data;
}

/**
 * The same endpoint the other way round: hex `Procore/InstanceId`s in, internal ids out.
 * See {@link resolveBimQueryInstanceIds} for the alignment contract.
 *
 * **Every id must be a 32-character hex string**, or the service answers `422` for the
 * whole array — which the legacy `Procore/Id` is not, so a caller mixing the two id
 * generations has to filter.
 */
export async function resolveBimQueryObjectIds(
    target: BimQueryTarget,
    params: { instanceIds: readonly string[]; signal?: AbortSignal },
): Promise<(ObjectId | null)[]> {
    const query = sliceParams(target);
    const response = await procoreClient<Envelope<(WireNumber | null)[]>>(
        `${sceneVersionUrl(target)}/objects/resolve`,
        {
            method: "POST",
            headers: { Accept: "application/json" },
            body: { ids: [...params.instanceIds] },
            ...(Object.keys(query).length ? { params: query } : {}),
            parseResponse: throwWhenBackfilling<Envelope<(WireNumber | null)[]>>((res) => res.json()),
            signal: params.signal,
        },
    );
    return response.data.map((id) => (id === null ? null : (Number(id) as ObjectId)));
}

/**
 * The ancestor matching `targetPredicate` per input — the index's `climb` operator, which
 * turns a picked triangle's owner into the object a user meant.
 *
 * The **union** of those ancestors, so one shared by several inputs appears once.
 * `view=FULL` because the caller wants the object it landed on: the properties ride along
 * rather than costing a second read.
 *
 * @param params.includeSelf Let an input already matching `targetPredicate` answer for
 *   itself instead of climbing past it. Defaults to `false` on the API.
 * @param params.stopPredicate A barrier: the walk halts at the first ancestor matching it,
 *   so an input whose match lies beyond contributes nothing.
 * @param params.policy `NEAREST` is the deepest match ("last object"), `TOPMOST` the
 *   shallowest ("first object").
 */
export async function climbBimQueryObjects(
    target: BimQueryTarget,
    params: {
        ids: BimQueryIds | BimQueryRoaringIds;
        targetPredicate: SearchPredicate;
        stopPredicate?: SearchPredicate;
        includeSelf?: boolean;
        policy: BimQueryClimbPolicy;
        perPage?: number;
        signal?: AbortSignal;
    },
): Promise<{ items: BimQueryObject[]; degraded: boolean }> {
    return procoreClient<{ items: BimQueryObject[]; degraded: boolean }>(`${sceneVersionUrl(target)}/objects/climb`, {
        method: "POST",
        body: climbBody(params),
        params: {
            id_kind: ID_KIND,
            view: "FULL",
            policy: params.policy,
            per_page: params.perPage ?? BIM_QUERY_TREE_PAGE_LIMIT,
            ...sliceParams(target),
        },
        parseResponse: throwWhenBackfilling(async (res) => {
            const body = (await res.json()) as Envelope<WireObject[]>;
            const items = body.data.map(parseObject).filter((object): object is BimQueryObject => object !== undefined);
            return { items, degraded: body.meta?.degraded === true };
        }),
        signal: params.signal,
    });
}

/**
 * The same ancestor union {@link climbBimQueryObjects} answers, as one bitmap — the whole
 * set in one response, where the JSON representation is offset-paged and carries a full
 * object per row the ids alone do not need.
 *
 * No `id_kind`, `view` or `per_page`: the API rejects all three alongside the bitmap
 * `Accept`, since the body is the entire set and its ids are internal by construction.
 */
export async function climbBimQueryIdSet(
    target: BimQueryTarget,
    params: {
        ids: readonly ObjectId[] | Uint32Array | BimQueryRoaringIds;
        targetPredicate: SearchPredicate;
        stopPredicate?: SearchPredicate;
        includeSelf?: boolean;
        policy: BimQueryClimbPolicy;
        signal?: AbortSignal;
    },
): Promise<BimQueryIdSet> {
    return procoreClient<BimQueryIdSet>(`${sceneVersionUrl(target)}/objects/climb`, {
        method: "POST",
        headers: { Accept: BIM_QUERY_ID_SET_MEDIA_TYPE },
        body: climbBody(params),
        params: { policy: params.policy, ...sliceParams(target) },
        parseResponse: throwWhenBackfilling(parseIdSet),
        signal: params.signal,
    });
}

/**
 * Everything a selection stands for, as one bitmap: the climb {@link climbBimQueryObjects}
 * runs, with each resolved ancestor expanded back over its subtree.
 *
 * Its own endpoint rather than a client-side composition, which would mean paging the whole
 * ancestor union out just to send it back in. Takes `climb`'s exact body, so the two can be
 * issued **concurrently** — both are keyed on the picked ids, not on each other's answer.
 *
 * Unlike {@link listBimQueryDescendantIdSet}, no `id_kind`: it only ever described this
 * response, and the API rejects it alongside the bitmap `Accept`.
 */
export async function resolveBimQuerySelectionIdSet(
    target: BimQueryTarget,
    params: {
        ids: readonly ObjectId[];
        targetPredicate: SearchPredicate;
        stopPredicate?: SearchPredicate;
        includeSelf?: boolean;
        policy: BimQueryClimbPolicy;
        signal?: AbortSignal;
    },
): Promise<BimQueryIdSet> {
    return procoreClient<BimQueryIdSet>(`${sceneVersionUrl(target)}/objects/selection`, {
        method: "POST",
        headers: { Accept: BIM_QUERY_ID_SET_MEDIA_TYPE },
        body: climbBody(params),
        params: { policy: params.policy, ...sliceParams(target) },
        parseResponse: throwWhenBackfilling(parseIdSet),
        signal: params.signal,
    });
}

/**
 * The volume enclosing a set of objects — the engine's "zoom to selection" primitive,
 * folded over the index's synthetic `$bounds/*` columns in one native call.
 *
 * A fold over **exactly the ids given**: the endpoint's `scope_id` is what frames a
 * subtree, and this client does not send one, so a grouping node contributes only its
 * own geometry. `null` bounds means no member of the selection had any at all — which is
 * also what a scan deadline yields, since the engine never answers a partial volume — and
 * the caller's cue to leave the camera alone.
 *
 * Unpaged: the answer is one computed shape rather than a page of rows. The `ids` list
 * has no ceiling on the endpoint, so the client batches at {@link
 * BIM_QUERY_BOUNDS_ID_LIMIT} for the request-body reason given there, and the caller
 * folds the batches — which is sound, since an enclosing volume of the parts encloses the
 * whole.
 *
 * `ids` must be either all numbers or all strings, same as
 * `objects/expand` and `objects/resolve`: the encoding is named per element by its JSON
 * type, not by `id_kind` — see that param below.
 */
export async function getBimQueryBounds(
    target: BimQueryTarget,
    params: {
        ids: readonly number[] | readonly string[];
        /** The API defaults to `BOX`; this client always states what it wants. */
        parts: BimQueryBoundsParts;
        signal?: AbortSignal;
    },
): Promise<{ bounds: BimQueryComputedBounds | null; degraded: boolean }> {
    return procoreClient<{ bounds: BimQueryComputedBounds | null; degraded: boolean }>(
        `${sceneVersionUrl(target)}/objects/bounds`,
        {
            // `POST` rather than the `QUERY` the API also routes this on: `QUERY` is
            // undocumented on purpose and needs a fallback anyway.
            method: "POST",
            // As on `objects/expand`, a JSON array of numbers *is* the internal-id
            // encoding, so the `id_kind` below names only `scope_id` — which this client
            // never sends.
            body: { ids: [...params.ids], parts: params.parts },
            params: { id_kind: ID_KIND, ...sliceParams(target) },
            parseResponse: throwWhenBackfilling(async (res) => {
                const body = (await res.json()) as Envelope<WireComputedBounds | null>;
                return {
                    bounds: body.data ? parseComputedBounds(body.data) : null,
                    degraded: body.meta?.degraded === true,
                };
            }),
            signal: params.signal,
        },
    );
}

/**
 * Whether this scene version has an index the API will answer queries from.
 *
 * Asks for one row of the tree's top level, which is the cheapest read that needs the
 * index to actually be mounted: a `200` means it is there, and everything else means it
 * is not usable *now* — a `202` while the service fetches it, a `404` for a scene
 * version that has none, a `403` for a caller who may not read it.
 *
 * Deliberately **not** wrapped in {@link throwWhenBackfilling}, unlike every other read
 * here: a probe must not raise the app-wide "preparing" signal — the backfill recovery
 * poll asks exactly this while the banner is already up, and a caller with another
 * backend has nothing for the user to wait for. Non-2xx still rejects, as core-http
 * rejects everything non-2xx, so a caller wanting a plain boolean catches.
 *
 * A `LIGHT` read, so the answer also covers the object metadata it joins in.
 */
export async function probeBimQuery(target: BimQueryTarget, params?: { signal?: AbortSignal }): Promise<boolean> {
    return procoreClient<boolean>(`${sceneVersionUrl(target)}/objects/${BIM_QUERY_ROOT}/children`, {
        method: "GET",
        params: { id_kind: ID_KIND, per_page: 1, view: "LIGHT", ...sliceParams(target) },
        parseResponse: (res) => res.status === 200,
        signal: params?.signal,
    });
}
